import {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {stepName} from '../../../config/nodeCategories';
import {buildAvailableVariables} from '../../../config/nodeVariables';
import AutomationTextField from '../variables/AutomationTextField';
import NodeEditorLayout, {NodeEditorState} from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';
import EmailBuilderField, {EmailBuilderHandle} from './EmailBuilderField';
import useTeamEmailSteps from './useTeamEmailSteps';

/**
 * Send team email: the email itself — subject and builder content, which fills the dialog's height. Who gets it and
 * which files are attached sit in the identity panel (`SendTeamEmailIdentityAside`).
 */
const SendTeamEmailEditorV2 = (props: NodeEditorV2Props) => {
    const {nodeConfig, setNodeConfig, upstreamNodes = [], registerBeforeSave, expanded, readOnly} = props;
    const {t} = useTranslation();
    const builder = useRef<EmailBuilderHandle>(null);
    const availableVariables = useMemo(() => buildAvailableVariables(upstreamNodes), [upstreamNodes]);
    const {teamStep, attachmentIds, missingAttachments} = useTeamEmailSteps(props);
    const subject: string = nodeConfig.subject || '';
    const content: string = nodeConfig.content || '';
    const write = (patch: Record<string, any>) => setNodeConfig(previous => ({...previous, ...patch}));
    useEffect(() => {
        registerBeforeSave?.(async () => {
            try {
                const saved = await builder.current?.flush();
                return {config: {...nodeConfig, ...saved, emailMode: 'BUILDER'}};
            } catch { return false; }
        });
        return () => registerBeforeSave?.(null);
    }, [registerBeforeSave, nodeConfig]);
    const {state, summary}: {state: NodeEditorState; summary: string} = !teamStep
        ? {state: 'wontRun', summary: t('This step writes to the team member a run is for. Put it below a Team members step, and above any Wait for everyone.')}
        : missingAttachments
            ? {state: 'wontRun', summary: t('A selected attachment step is unavailable on this path. Choose an earlier file step before sending.')}
            : !subject.trim() || !content.trim()
                ? {state: 'wontRun', summary: t('Add a subject and content, this step cannot send without them.')}
                : {state: 'configured', summary: [
                    t('Sends ‘{{subject}}’ to each team member of ‘{{step}}’, one email per person.', {subject, step: stepName(teamStep)}),
                    attachmentIds.length ? t('All files from the selected steps are required attachments.') : ''
                ].filter(Boolean).join(' ')};

    return <NodeEditorLayout state={state} summary={summary} fill>
        <AutomationTextField insertion={'step-value'} disabled={readOnly} label={t('Subject')}
                             value={subject} onChange={value => write({subject: value})} variables={availableVariables}/>
        <EmailBuilderField ref={builder} content={content} unlayerSourceData={nodeConfig.unlayerSourceData}
                           onChange={write} variables={availableVariables} expanded={expanded} fill readOnly={readOnly}/>
    </NodeEditorLayout>;
};

export default SendTeamEmailEditorV2;
